<template>
  <v-dialog v-model="visible" max-width="900" persistent scrollable>
    <v-card class="skill-editor">
      <v-toolbar density="compact" color="surface" flat>
        <v-toolbar-title class="text-body-1 font-weight-medium">
          <v-icon v-if="readOnly" size="small" class="mr-1">mdi-lock-outline</v-icon>
          {{ readOnly ? t('skill.viewTitle', { name: form.name }) : isNew ? t('skill.newSkill') : t('skill.editTitle', { name: form.name }) }}
        </v-toolbar-title>
        <v-btn icon="mdi-close" variant="text" size="small" @click="cancel" />
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-form ref="formRef" @submit.prevent="submit">
          <v-alert
            v-if="readOnly"
            type="info"
            variant="tonal"
            density="compact"
            class="ma-4 mb-0"
          >
            {{ t('skill.readOnlyHint') }}
          </v-alert>

          <div class="skill-editor__section">
            <div class="d-flex align-center ga-4">
              <v-text-field
                v-model="form.name"
                :label="t('skill.name')"
                placeholder="review-rfi"
                :hint="t('skill.nameHint')"
                :rules="[nameRule]"
                :disabled="!isNew"
                density="compact"
                hide-details="auto"
                class="flex-grow-1"
              />
              <v-switch
                v-model="form.shared"
                :label="t('skill.shared')"
                :disabled="readOnly"
                density="compact"
                hide-details
                color="primary"
                class="flex-shrink-0"
              />
            </div>

            <v-textarea
              v-model="form.description"
              :label="t('skill.description')"
              :placeholder="t('skill.descriptionPlaceholder')"
              :rules="[v => !!v || t('skill.required')]"
              :disabled="readOnly"
              density="compact"
              rows="2"
              auto-grow
              no-resize
              hide-details="auto"
              class="mt-3"
            />

            <v-row dense class="mt-3">
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="form.tags"
                  :label="t('skill.tags')"
                  :placeholder="t('skill.tagsPlaceholder')"
                  :disabled="readOnly"
                  multiple
                  chips
                  closable-chips
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="form.allowedTools"
                  :label="t('skill.allowedTools')"
                  :placeholder="t('skill.allToolsAllowed')"
                  :items="toolItems"
                  item-title="title"
                  item-value="value"
                  :disabled="readOnly"
                  multiple
                  chips
                  closable-chips
                  :loading="toolsLoading"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
          </div>

          <v-divider />

          <div class="skill-editor__section skill-editor__section--grow">
            <v-label class="text-caption text-medium-emphasis mb-2 d-block">
              {{ t('skill.instructions') }}
            </v-label>
            <MarkdownEditor v-if="!readOnly" v-model="form.content" />
            <div v-else class="skill-editor__readonly markdown-body" v-html="renderedContent" />
            <div v-if="contentError" class="text-error text-caption mt-1">{{ t('skill.contentRequired') }}</div>
          </div>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-3">
        <v-btn
          v-if="!isNew && !readOnly"
          color="error"
          variant="tonal"
          size="small"
          prepend-icon="mdi-delete-outline"
          :loading="deleting"
          @click="confirmDelete"
        >
          {{ t('common.delete') }}
        </v-btn>
        <v-spacer />
        <v-btn variant="text" @click="cancel">{{ readOnly ? t('common.close') : t('common.cancel') }}</v-btn>
        <v-btn v-if="!readOnly" color="primary" variant="flat" :loading="saving" @click="submit">
          {{ isNew ? t('common.create') : t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <ConfirmDialog ref="confirmDialog" />
  </v-dialog>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed, onMounted } from 'vue'
import { listTools } from '../../api/tools.js'
import { useMarkdown } from '../../composables/useMarkdown.js'
import ConfirmDialog from './ConfirmDialog.vue'
import MarkdownEditor from './MarkdownEditor.vue'

const { render: renderMarkdown } = useMarkdown()

const visible = ref(false)
const isNew = ref(true)
// Built-in skills (global, maintained by RDrive) come back readOnly: the
// dialog turns into a viewer — locked fields, rendered content, no save/delete.
const readOnly = ref(false)
const saving = ref(false)
const deleting = ref(false)
const formRef = ref(null)
const confirmDialog = ref(null)

const form = ref(emptyForm())
const contentError = ref(false)
const renderedContent = computed(() => renderMarkdown(form.value.content || ''))

let resolveFn = null

const nameRule = v =>
  /^[a-z0-9]+(-[a-z0-9]+)*$/.test(v) || t('skill.nameRule')

const toolsLoading = ref(false)
const toolItems = ref([])

function humanize(name) {
  return name
    .replace(/^rdrive_/, '')
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

async function fetchTools() {
  if (toolItems.value.length) return
  toolsLoading.value = true
  try {
    const categories = await listTools()
    const items = []
    for (const cat of categories.sort((a, b) => a.category.localeCompare(b.category))) {
      items.push({ type: 'subheader', title: cat.category.charAt(0).toUpperCase() + cat.category.slice(1) })
      for (const tool of cat.tools) {
        items.push({ title: humanize(tool.name), value: tool.name })
      }
    }
    toolItems.value = items
  } catch {
    // Backend may not expose /tools — leave the field empty.
  } finally {
    toolsLoading.value = false
  }
}

onMounted(fetchTools)

function emptyForm() {
  return {
    name: '',
    description: '',
    content: '',
    tags: [],
    allowedTools: [],
    shared: false,
  }
}

function open(skill) {
  readOnly.value = !!skill?.readOnly
  if (skill) {
    isNew.value = false
    form.value = {
      name: skill.name,
      description: skill.description || '',
      content: skill.content || '',
      tags: skill.tags ? [...skill.tags] : [],
      allowedTools: skill.allowedTools ? [...skill.allowedTools] : [],
      shared: skill.shared || false,
    }
  } else {
    isNew.value = true
    form.value = emptyForm()
  }
  saving.value = false
  deleting.value = false
  visible.value = true
  return new Promise(resolve => { resolveFn = resolve })
}

async function submit() {
  if (readOnly.value) return
  const { valid } = await formRef.value.validate()
  contentError.value = !form.value.content
  if (!valid || contentError.value) return
  saving.value = true
  resolveFn?.({ action: 'save', form: { ...form.value } })
}

async function confirmDelete() {
  const ok = await confirmDialog.value.open({
    title: t('skill.deleteTitle'),
    message: t('skill.deleteMessage', { name: form.value.name }),
  })
  if (ok) {
    deleting.value = true
    resolveFn?.({ action: 'delete', name: form.value.name, shared: form.value.shared })
  }
}

function cancel() {
  visible.value = false
  resolveFn?.(null)
}

function close() {
  visible.value = false
}

defineExpose({ open, close })
</script>

<style scoped>
.skill-editor :deep(.v-card-text) {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.skill-editor__section {
  padding: 16px 20px;
}

.skill-editor__section--grow {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.skill-editor__section--grow .markdown-editor {
  flex: 1;
  min-height: 0;
}

/* MarkdownEditor's .markdown-body styles are scoped to it, so the read-only
   view carries its own minimal typography for the v-html content. */
.skill-editor__readonly {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 1.6;
}

.skill-editor__readonly :deep(h1),
.skill-editor__readonly :deep(h2),
.skill-editor__readonly :deep(h3) {
  margin: 12px 0 6px;
  line-height: 1.3;
}

.skill-editor__readonly :deep(p),
.skill-editor__readonly :deep(ul),
.skill-editor__readonly :deep(ol),
.skill-editor__readonly :deep(pre) {
  margin: 0 0 8px;
}

.skill-editor__readonly :deep(ul),
.skill-editor__readonly :deep(ol) {
  padding-left: 20px;
}

.skill-editor__readonly :deep(pre),
.skill-editor__readonly :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8125rem;
}

.skill-editor__readonly :deep(pre) {
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  overflow-x: auto;
}
</style>
